import { Link, NavLink, Outlet } from 'react-router';
import {
  BanknoteIcon,
  BriefcaseIcon,
  BuildingIcon,
  FileTextIcon,
  LogOutIcon,
  ReceiptIcon,
  UsersIcon,
  CreditCardIcon,
  UserCheckIcon,
  WalletIcon,
} from 'lucide-react';

import vizitProIcon from '@/assets/brand/icon-64.png';
import { useUser } from '@/components/auth-provider';
import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { TrialBanner } from '@/components/trial-banner';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useLogout } from '@/hooks/use-logout';

import { LangToggle } from './lang-toggle';

// ─── Nav config ───────────────────────────────────────────────────────────────

function useNavItems() {
  const { t } = useLanguage();
  const { has } = useEntitlements();

  return [
    { to: '/jobs', label: t.jobs, icon: BriefcaseIcon },
    { to: '/quotes', label: t.quotesNav, icon: FileTextIcon },
    { to: '/invoices', label: t.invoicesNav, icon: ReceiptIcon },
    { to: '/customers', label: t.customersNav, icon: UsersIcon },
    { to: '/payments', label: t.paymentsNav, icon: CreditCardIcon },
    ...(has('employees')
      ? [{ to: '/employees', label: t.employees, icon: UserCheckIcon }]
      : []),
    ...(has('payroll')
      ? [{ to: '/payroll', label: t.payrollNav, icon: BanknoteIcon }]
      : []),
    { to: '/business-page', label: t.businessPageNav, icon: BuildingIcon },
    { to: '/billing', label: t.billingNav, icon: WalletIcon },
  ] as const;
}

// ─── Desktop sidebar ──────────────────────────────────────────────────────────

function Sidebar() {
  const { user } = useUser();
  const { t } = useLanguage();
  const handleLogout = useLogout();
  const navItems = useNavItems();

  return (
    <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r bg-card h-svh sticky top-0">
      {/* Brand */}
      <div className="flex items-center gap-2 px-5 py-4 border-b">
        <Link to="/jobs" className="flex items-center gap-2 text-lg font-bold tracking-tight">
          <img src={vizitProIcon} alt="" className="w-7 h-7 rounded-md" />
          VizitPro
        </Link>
      </div>

      {/* Business info — shows the first membership until Task 5 adds a
          proper active-business selector for multi-business users. */}
      {user?.businesses[0] && (
        <div className="px-5 py-3 border-b">
          <p className="text-xs font-semibold truncate">
            {user.businesses[0].business_name}
          </p>
        </div>
      )}

      {/* Nav */}
      <nav className="flex flex-col gap-0.5 p-3 flex-1">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              ].join(' ')
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t flex flex-col gap-2">
        <div className="flex items-center justify-between px-2">
          <span className="text-xs text-muted-foreground truncate max-w-[130px]">
            {user?.email}
          </span>
          <LangToggle />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground hover:text-foreground gap-2"
          onClick={handleLogout}
        >
          <LogOutIcon className="w-4 h-4" />
          {t.signOut}
        </Button>
      </div>
    </aside>
  );
}

// ─── Mobile header ────────────────────────────────────────────────────────────

function MobileHeader() {
  const { user } = useUser();
  const handleLogout = useLogout();

  return (
    <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b bg-card sticky top-0 z-10">
      <Link to="/jobs" className="flex items-center gap-2 text-base font-bold tracking-tight">
        <img src={vizitProIcon} alt="" className="w-6 h-6 rounded-md" />
        VizitPro
        {user?.businesses[0] && (
          <span className="text-xs font-normal text-muted-foreground ml-2">
            {user.businesses[0].business_name}
          </span>
        )}
      </Link>
      <div className="flex items-center gap-2">
        <LangToggle />
        <Button
          variant="ghost"
          size="icon"
          className="w-8 h-8 text-muted-foreground"
          onClick={handleLogout}
        >
          <LogOutIcon className="w-4 h-4" />
        </Button>
      </div>
    </header>
  );
}

// ─── Mobile bottom nav ────────────────────────────────────────────────────────

function MobileNav() {
  const navItems = useNavItems();

  // Show only 5 on mobile bottom bar (most common); rest are accessible via routes
  const mobileItems = navItems.slice(0, 5);

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-10 border-t bg-card">
      <div className="flex items-stretch">
        {mobileItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              [
                'flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors min-w-0',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')
            }
          >
            <Icon className="w-5 h-5 shrink-0" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// ─── App Layout ───────────────────────────────────────────────────────────────

export function AppLayout() {
  return (
    <div className="flex min-h-svh bg-background">
      <Sidebar />

      <div className="flex flex-col flex-1 min-w-0">
        <MobileHeader />
        <TrialBanner />

        {/* Page content — extra bottom padding on mobile for the nav bar */}
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6">
          <Outlet />
        </main>
      </div>

      <MobileNav />
    </div>
  );
}
